import {ReactNode} from 'react';
import {
    createBrowserRouter,
    RouterProvider
} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {
    createAuthAwareQueryClient,
    SessionGuardProvider
} from '@flexinet/auth-components';
import {theme} from "./theme.ts";
import {MantineProvider} from "@mantine/core";
import {Notifications} from "@mantine/notifications";
import {ModalsProvider} from "@mantine/modals";

// FNP-1936: every stylesheet loads here, once. mantine-datatable must come
// after @mantine/core or the list tables render unstyled and the sort arrow
// never flips direction (FNP-1859).
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-datatable/styles.css';
import '@flexinet/ui-components/style.css';
import '@fontsource/inter';

import {AuthProvider} from "./providers/AuthProvider.tsx";
import {UserAttributesProvider} from "./providers/UserAttributesProvider.tsx";
import {PermissionsProvider} from "./providers/PermissionsProvider.tsx";
import {routes} from './config/routes.tsx';

const queryClient = createAuthAwareQueryClient();

const router = createBrowserRouter(routes)


// FNP-1731: the provider tree is exported on its own so a test can mount it
// exactly as the app does, without the router.
// FNP-1936: no UserAccountProvider, because nothing in this app reads the
// platform Account record.
export const AppProviders = ({children}: { children?: ReactNode }) => {

    return (
        <MantineProvider theme={theme}>
            <Notifications position={'bottom-left'}/>
            <SessionGuardProvider>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                        <UserAttributesProvider>
                            <PermissionsProvider>
                                <ModalsProvider>
                                    {children}
                                </ModalsProvider>
                            </PermissionsProvider>
                        </UserAttributesProvider>
                    </AuthProvider>
                </QueryClientProvider>
            </SessionGuardProvider>
        </MantineProvider>
    );
}

export const App = () => {

    return (
        <AppProviders>
            <RouterProvider router={router}/>
        </AppProviders>
    );
}
